import { ProductManifestSchema, type OfferSku, type ProductManifest } from "../domain/schema";
import { launchGrowthResources, productionResources } from "./expansion";

export interface MarketPack {
  id: string;
  name: string;
  audience: string;
  promise: string;
  angles: string[];
  requiredOffer: OfferSku;
}

export interface InteriorModule {
  id: string;
  name: string;
  purpose: string;
  pageSystem: string;
  requiredOffer: OfferSku;
}

export interface VisualDirection {
  id: string;
  name: string;
  character: string;
  composition: string;
  palette: string;
  requiredOffer: OfferSku;
}

export interface Blueprint {
  id: string;
  name: string;
  market: string;
  positioning: string;
  titles: Array<{ title: string; subtitle: string; differentiator: string }>;
  requiredOffer: OfferSku;
}

export interface DownloadAsset {
  id: string;
  name: string;
  description: string;
  type: "Template" | "Worksheet" | "License" | "Campaign" | "SOP";
  requiredOffer: OfferSku;
  content: string;
}

export const productManifest: ProductManifest = ProductManifestSchema.parse({
  schemaVersion: 1,
  productSlug: "paper-kingdom",
  productVersion: "1.1.0",
  name: "Paper Kingdom",
  description: "A professional planning and prompt-composition studio for coordinated publishing catalogues.",
  theme: {
    paper: "#f4efe3",
    ink: "#28241f",
    forest: "#29463a",
    brass: "#b48645",
  },
  offers: [
    { sku: "paper-kingdom-fe", name: "Paper Kingdom", price: 17 },
    { sku: "paper-kingdom-unlimited", name: "Unlimited", price: 47 },
    { sku: "paper-kingdom-dfy", name: "Done-for-you Blueprints", price: 67 },
    { sku: "paper-kingdom-production", name: "Production Vault", price: 67 },
    { sku: "paper-kingdom-launch-growth", name: "Launch & Growth", price: 67 },
    { sku: "paper-kingdom-reseller", name: "Reseller", price: 97 },
  ],
  counts: {
    corePrompts: 150,
    starterMarkets: 7,
    starterModules: 12,
    visualDirections: 9,
  },
});

const starterMarkets: MarketPack[] = [
  {
    id: "practical-home-knowledge",
    name: "Practical Home Knowledge",
    audience: "Homeowners and renters who want clear, safety-aware checklists and records",
    promise: "Turn years of practical experience into useful home guides without teaching risky repairs",
    angles: ["maintenance records", "problem notes", "questions for a tradesperson", "seasonal checks"],
    requiredOffer: "paper-kingdom-fe",
  },
  {
    id: "mindful-living",
    name: "Mindful Living",
    audience: "Busy adults who want realistic, low-pressure wellbeing practices",
    promise: "Turn small reflective rituals into a sustainable daily rhythm",
    angles: ["five-minute rituals", "gentle reflection", "digital boundaries", "seasonal resets"],
    requiredOffer: "paper-kingdom-fe",
  },
  {
    id: "home-organization",
    name: "Calm Home Systems",
    audience: "Households seeking practical organization without perfectionism",
    promise: "Create repeatable home systems that reduce friction and visual noise",
    angles: ["room-by-room reset", "family routines", "decluttering decisions", "maintenance plans"],
    requiredOffer: "paper-kingdom-fe",
  },
  {
    id: "creative-kids",
    name: "Creative Kids",
    audience: "Parents and educators looking for screen-light creative activities",
    promise: "Make independent play easier to start, finish, and feel proud of",
    angles: ["story starters", "drawing quests", "observation games", "rainy-day challenges"],
    requiredOffer: "paper-kingdom-fe",
  },
  {
    id: "small-business",
    name: "Small Business Clarity",
    audience: "Solo service providers building a more organized, sustainable business",
    promise: "Convert ambitious goals into visible decisions and weekly action",
    angles: ["offer clarity", "content planning", "client workflow", "quarterly review"],
    requiredOffer: "paper-kingdom-fe",
  },
  {
    id: "travel-memories",
    name: "Travel & Memory",
    audience: "Travellers who want to notice, record, and remember more",
    promise: "Capture the texture of a trip without turning it into homework",
    angles: ["pre-trip anticipation", "daily field notes", "food memories", "post-trip keepsakes"],
    requiredOffer: "paper-kingdom-fe",
  },
  {
    id: "pet-companions",
    name: "Pet Companions",
    audience: "Pet owners who value routines, records, and meaningful memories",
    promise: "Keep practical care details and the story of a shared life together",
    angles: ["care log", "training wins", "health questions", "memory journal"],
    requiredOffer: "paper-kingdom-fe",
  },
];

const unlimitedMarketSeeds = [
  ["garden-seasons", "Garden Through the Seasons", "Home gardeners building confidence through observation", "Plan, notice, and learn from one growing season to the next"],
  ["teacher-planning", "Calm Classroom", "Teachers who need practical planning and reflection systems", "Reduce preparation friction while protecting teaching energy"],
  ["homeschool", "Curious Homeschool", "Home educators who value flexible, child-led structure", "Turn learning intentions into adaptable weekly adventures"],
  ["wedding-planning", "Meaningful Wedding", "Couples planning a personal celebration with clear priorities", "Make hundreds of decisions feel coherent and shared"],
  ["new-parent", "The New Parent Season", "New parents who need gentle prompts rather than rigid advice", "Preserve memories and reduce mental load without pressure"],
  ["caregiver", "Caregiver Compass", "Family caregivers coordinating information and their own wellbeing", "Bring calm structure to appointments, observations, and support"],
  ["reading-life", "A Richer Reading Life", "Readers who want to remember and connect ideas across books", "Turn reading notes into a lasting personal knowledge trail"],
  ["food-journal", "Kitchen Stories", "Home cooks exploring recipes, traditions, and shared meals", "Record what worked and the stories worth carrying forward"],
  ["fitness-consistency", "Movement Consistency", "Adults rebuilding a sustainable relationship with exercise", "Track effort, energy, and progress without all-or-nothing pressure"],
  ["sleep-routines", "Evening Reset", "Adults improving sleep-supportive routines without medical claims", "Design a calmer transition from busy day to restorative night"],
  ["student-focus", "Student Focus", "Secondary and university students managing complex workloads", "Translate deadlines into realistic study rhythms and reviews"],
  ["language-learning", "Language Journey", "Independent language learners seeking visible momentum", "Connect daily practice, real-world use, and reflective review"],
  ["music-practice", "Purposeful Practice", "Amateur musicians who want more rewarding practice sessions", "Replace vague repetition with deliberate, trackable musical work"],
  ["photography", "Everyday Photography", "Hobby photographers developing a consistent visual voice", "Move from isolated images to intentional bodies of work"],
  ["family-history", "Family Story Archive", "Families preserving memories with care and context", "Gather stories in a form future generations can understand"],
  ["grief-reflection", "Gentle Remembrance", "Adults seeking a private, non-clinical space for remembrance", "Hold memories and changing feelings without prescribing a timeline"],
  ["career-change", "Career Transition", "Professionals exploring a thoughtful next chapter", "Turn uncertainty into evidence, experiments, and informed choices"],
  ["freelance", "Freelance Studio", "Independent creatives building dependable operating habits", "Connect pipeline, projects, money, and creative energy"],
  ["digital-detox", "Intentional Technology", "People reshaping attention and device habits", "Replace reactive screen time with chosen, workable boundaries"],
  ["nature-study", "Nature Observer", "Families and adults learning through nearby nature", "Develop a practice of noticing, recording, and asking better questions"],
  ["gratitude-depth", "Beyond the Gratitude List", "Reflective adults who want more specific appreciation practices", "Notice meaning and connection without forced positivity"],
  ["retirement", "A Purposeful Retirement", "New retirees designing a fulfilling weekly life", "Balance freedom, contribution, connection, and rest"],
  ["neurodivergent-planning", "Flexible Focus", "Neurodivergent adults seeking adaptable planning supports", "Externalize decisions and make plans easier to restart"],
  ["local-adventures", "Closer to Home", "Families and couples wanting memorable low-cost local outings", "Turn nearby places into a year of intentional discovery"],
] as const;

export const marketPacks: MarketPack[] = [
  ...starterMarkets,
  ...unlimitedMarketSeeds.map(([id, name, audience, promise]) => ({
    id,
    name,
    audience,
    promise,
    angles: ["identity-led positioning", "practical progression", "reflective review", "catalogue expansion"],
    requiredOffer: "paper-kingdom-unlimited" as const,
  })),
];

const starterModuleSeeds = [
  ["daily-check-in", "Daily Check-in", "Create a quick recurring reflection", "Date, state, priority, small action, closing note"],
  ["weekly-review", "Weekly Review", "Close a week and improve the next", "Wins, friction, evidence, lesson, next-week adjustment"],
  ["guided-journal", "Guided Journal Spread", "Support deeper reflection without overwhelming the page", "Opening thought, three sequenced prompts, writing space, gentle close"],
  ["habit-tracker", "Evidence Record", "Keep supporting evidence and context connected to each record", "Date, related record, evidence type, source or file location, notes"],
  ["project-planner", "Project Planner", "Translate a result into bounded work", "Outcome, constraints, milestones, next action, review date"],
  ["decision-page", "Decision Page", "Make a considered choice visible", "Decision, options, criteria, trade-offs, commitment"],
  ["brain-dump", "Guided Brain Dump", "Capture and sort mental load", "Capture area, do, defer, delegate, release"],
  ["monthly-map", "Monthly Map", "Set a realistic monthly direction", "Theme, commitments, capacity, key dates, success signals"],
  ["reflection-wheel", "Reflection Wheel", "Review several life areas at a glance", "Rated areas, evidence notes, one focus, one boundary"],
  ["challenge-page", "Seven-day Challenge", "Create a short, progressive experience", "Daily action, friction plan, check box, reflection cue"],
  ["resource-log", "Resource Log", "Keep useful references actionable", "Source, key idea, relevance, next use, citation"],
  ["closing-review", "Completion Review", "Extract value at the end of a book", "Before and after, strongest evidence, lessons, continuation plan"],
] as const;

const advancedModuleNames = [
  "90-day roadmap",
  "values sort",
  "identity statement",
  "energy audit",
  "time reality log",
  "friction finder",
  "trigger-action plan",
  "progress dashboard",
  "milestone retrospective",
  "scenario planner",
  "priority matrix",
  "idea scorecard",
  "customer interview log",
  "content pillar map",
  "offer clarity canvas",
  "launch calendar",
  "budget snapshot",
  "meal rhythm planner",
  "home reset checklist",
  "travel field notes",
  "memory interview",
  "reading connection map",
  "practice session plan",
  "creative constraint card",
  "observation record",
  "experiment log",
  "before-and-after inventory",
  "conversation prep",
  "appointment brief",
  "support network map",
  "boundary builder",
  "rest menu",
  "seasonal review",
  "annual index",
  "collection tracker",
  "next-volume bridge",
];

export const interiorModules: InteriorModule[] = [
  ...starterModuleSeeds.map(([id, name, purpose, pageSystem]) => ({
    id,
    name,
    purpose,
    pageSystem,
    requiredOffer: "paper-kingdom-fe" as const,
  })),
  ...advancedModuleNames.map((name, index) => ({
    id: `advanced-${String(index + 1).padStart(2, "0")}`,
    name: name.replace(/\b\w/g, (letter) => letter.toUpperCase()),
    purpose:
      name === "observation record"
        ? "Capture what happened, where it happened, and what the reader noticed"
        : name === "conversation prep"
          ? "Organise useful observations and questions before a conversation"
          : `Give the reader a structured ${name} that produces a clear, usable record.`,
    pageSystem: "Orientation, concise instructions, bounded inputs, evidence field, action close",
    requiredOffer: "paper-kingdom-unlimited" as const,
  })),
];

export const visualDirections: VisualDirection[] = [
  ["modern-botanical", "Soft & Organic", "Grounded, spacious, quietly premium", "One organic focal form with disciplined type and negative space", "Choose any product palette"],
  ["editorial-minimal", "Modern & Professional", "Intelligent, restrained, contemporary", "Type-led grid, fine rules, confident whitespace", "Choose any product palette"],
  ["heritage-library", "Classic & Established", "Collected, scholarly, enduring", "Framed title field, subtle ornament, archival hierarchy", "Choose any product palette"],
  ["playful-cut-paper", "Playful & Bright", "Tactile, optimistic, imaginative", "Layered geometric paper shapes and bold friendly type", "Choose any product palette"],
  ["soft-geometric", "Gentle & Organised", "Orderly, modern, reassuring", "Repeated modular motif with rounded geometry", "Choose any product palette"],
  ["field-notes", "Practical & Grounded", "Curious, practical, observant", "Annotated object, label system, notebook-like structure", "Choose any product palette"],
  ["quiet-luxury", "Elegant & Refined", "Refined, understated, giftable", "Small title field, material texture, exquisite proportion", "Choose any product palette"],
  ["bold-type", "Strong & Direct", "Direct, energetic, highly legible", "Overscale title, tight grid, small supporting mark", "Choose any product palette"],
  ["science-grid", "Scientific & Precise", "Evidence-led, exact, technical", "Measured information grid with one diagram-led focal area", "Choose any product palette"],
].map(([id, name, character, composition, palette]) => ({
  id,
  name,
  character,
  composition,
  palette,
  requiredOffer: "paper-kingdom-fe" as const,
}));

visualDirections.push(
  ...[
    ["coastal-calm", "Coastal Calm", "Airy, restorative, natural"],
    ["museum-modern", "Museum Modern", "Curated, cultured, precise"],
    ["folk-pattern", "Contemporary Folk", "Warm, expressive, handcrafted"],
    ["midnight-study", "Midnight Study", "Focused, atmospheric, sophisticated"],
    ["sunlit-studio", "Sunlit Studio", "Creative, open, quietly joyful"],
    ["technical-atlas", "Technical Atlas", "Systematic, capable, information-rich"],
    ["story-book", "Modern Storybook", "Narrative, charming, collected"],
    ["monochrome-object", "Monochrome Object", "Iconic, minimal, tactile"],
    ["retro-optimist", "Retro Optimist", "Confident, friendly, nostalgic"],
    ["naturalist-archive", "Naturalist Archive", "Observant, detailed, timeless"],
    ["colour-block", "Architectural Colour Block", "Bold, structured, contemporary"],
    ["hand-drawn-signal", "Hand-drawn Signal", "Human, energetic, approachable"],
  ].map(([id, name, character]) => ({
    id,
    name,
    character,
    composition: "A repeatable cover grid with one controlled variable per title",
    palette: "Purpose-built five-colour system with print-safe contrast",
    requiredOffer: "paper-kingdom-unlimited" as const,
  })),
);

const blueprintSeeds = [
  ["quiet-mornings", "The Quiet Morning Library", "Mindful living", "Short guided journals for creating a grounded first hour", ["Begin Gently", "Notice First", "Clear the Noise", "Choose the Day", "Make Room", "Carry the Calm"]],
  ["calm-home", "The Calm Home System", "Home organization", "Room-specific workbooks built around maintainable reset systems", ["The Entry Reset", "The Kitchen Rhythm", "The Clear Bedroom", "The Working Home", "The Family Flow", "The Seasonal Reset"]],
  ["young-makers", "Young Makers Club", "Creative kids", "Screen-light activity books that reward curiosity over perfect results", ["Invent a Creature", "Build a Tiny World", "Draw the Weather", "Stories from Objects", "The Colour Quest", "Make a Mini Museum"]],
  ["solo-studio", "The Solo Studio Desk", "Small business", "Focused planning workbooks for service-based solopreneurs", ["Name the Offer", "Find the Fit", "Plan the Pipeline", "Deliver with Ease", "Review the Numbers", "Design the Next Quarter"]],
  ["travel-noticing", "The Noticing Traveller", "Travel", "Compact journals that make travel memories more vivid and specific", ["Before You Go", "Streets & Signs", "At the Table", "People & Places", "Small Discoveries", "Bring It Home"]],
  ["garden-year", "The Observant Garden", "Gardening", "Seasonal field journals for learning from a garden in context", ["Winter Plans", "First Signs", "The Growing Map", "High Summer Notes", "Harvest Lessons", "The Garden Year"]],
  ["reading-threads", "Reading in Threads", "Reading", "Literary journals that connect ideas across books and time", ["The Reading Self", "Ideas in Common", "Character & Choice", "Place on the Page", "Lines Worth Keeping", "The Year in Books"]],
  ["family-archive", "Our Family Archive", "Family history", "Interview-led keepsake books with respectful context prompts", ["Where We Began", "Rooms We Remember", "Work & Craft", "Food at the Table", "Turning Points", "What We Carry"]],
  ["creative-practice", "The Creative Practice Set", "Creativity", "Process journals that help makers sustain and understand their work", ["Start Before Ready", "Useful Constraints", "The Idea Field", "Work in Progress", "Finish with Care", "Begin Again"]],
  ["student-semester", "The Clear Semester", "Student planning", "A six-part system for managing study with evidence and reflection", ["Map the Term", "Plan the Week", "Study with Purpose", "Prepare to Submit", "Review the Result", "Close the Semester"]],
  ["local-year", "A Year Close to Home", "Local adventure", "Seasonal family activity books built around nearby discovery", ["Winter Nearby", "Spring on Foot", "Summer Evenings", "Autumn Trails", "Rainy-day Finds", "Our Local Year"]],
  ["intentional-tech", "The Intentional Technology Set", "Digital wellbeing", "Non-judgmental workbooks for reshaping attention and device habits", ["See the Pattern", "Choose the Boundary", "Reclaim the Morning", "Make Space to Focus", "Reconnect Offline", "Keep What Works"]],
] as const;

export const blueprints: Blueprint[] = blueprintSeeds.map(
  ([id, name, market, positioning, titles]) => ({
    id,
    name,
    market,
    positioning,
    titles: titles.map((title, index) => ({
      title,
      subtitle: `${market} guide ${index + 1}: ${positioning}`,
      differentiator: `This book focuses on “${title}” so the reader can work on one useful part of ${market.toLowerCase()} at a time.`,
    })),
    requiredOffer: "paper-kingdom-dfy",
  }),
);

const asset = (
  id: string,
  name: string,
  description: string,
  type: DownloadAsset["type"],
  requiredOffer: OfferSku,
  content: string,
): DownloadAsset => ({ id, name, description, type, requiredOffer, content });

export const downloadAssets: DownloadAsset[] = [
  asset("listing-workbook", "Listing Quality Workbook", "A final-pass worksheet for title, subtitle, description, keywords, categories, and proof checks.", "Worksheet", "paper-kingdom-dfy", `# Listing Quality Workbook\n\n## Product truth\n- Exact format and dimensions:\n- Page count:\n- Intended reader:\n- Specific contents:\n- What this book does not claim:\n\n## Metadata alignment\n- Cover title exactly matches metadata: [ ]\n- Subtitle is accurate and non-repetitive: [ ]\n- Description reflects the interior: [ ]\n- Keywords are relevant and readable: [ ]\n- Categories were verified in the current marketplace: [ ]\n\n## Final proof\n- Rights verified: [ ]\n- AI disclosure decision recorded: [ ]\n- Barcode-safe area checked: [ ]\n- Printed proof reviewed: [ ]`),
  asset("cover-layout", "Editable Cover System Brief", "A structured cover-layout specification you can recreate in your preferred design tool.", "Template", "paper-kingdom-dfy", `# Editable Cover System Brief\n\n## Shared grid\nTrim size: \nSpine-width source: official marketplace calculator\nSafe areas: \nTitle field: \nImprint marker: \nSeries-family marker (not a KDP Series claim):\n\n## Locked variables\n- Type scale:\n- Alignment:\n- Illustration treatment:\n- Palette proportions:\n\n## Per-title variables\n- Focal symbol:\n- Accent colour:\n- Volume-specific descriptor:`),
  asset("interior-layout", "Interior Layout Specification", "A reusable production brief for consistent page systems.", "Template", "paper-kingdom-dfy", `# Interior Layout Specification\n\nTrim and bleed:\nMargins and gutter:\nBase grid:\nTypefaces and licences:\nBody size and leading:\nWriting-line spacing:\nNavigation system:\nAccessibility checks:\nInk-coverage target:\n\n## Master pages\n1. Front matter\n2. Section opener\n3. Guided exercise\n4. Tracker or log\n5. Reflection\n6. Closing review`),
  ...Array.from({ length: 10 }, (_, index) =>
    asset(
      `promo-brief-${index + 1}`,
      `Promotional Graphic Brief ${index + 1}`,
      "A campaign-ready creative direction for a branded promotional visual.",
      "Campaign",
      "paper-kingdom-reseller",
      `# Promotional Graphic Brief ${index + 1}\n\nObjective: ${["Introduce the complete system", "Show the six-book workflow", "Demonstrate prompt quality", "Explain the imprint lock", "Visualize catalogue consistency", "Highlight copy-and-export", "Position the starter packs", "Answer the no-credits objection", "Present the buyer workflow", "Make a clear final invitation"][index]}.\n\nFormat: 4:5 social graphic plus 16:9 adaptation\nMessage hierarchy: one outcome-led headline, one concrete proof point, one clear action\nVisual system: Paper Kingdom forest, parchment, clay, editorial serif, tactile paper detail\nGuardrails: no income promise, no marketplace logo misuse, no invented testimonial, no fake AI output\nDeliverables: final copy, layout map, image direction, accessibility alt text, adaptation notes.`,
    ),
  ),
  ...Array.from({ length: 5 }, (_, index) =>
    asset(
      `email-swipe-${index + 1}`,
      `Reseller Email Swipe ${index + 1}`,
      "Editable, claim-safe launch copy for a reseller campaign.",
      "Campaign",
      "paper-kingdom-reseller",
      `# Email ${index + 1}: ${["The scattered-catalogue problem", "A look inside the imprint lock", "From one idea to six distinct books", "What Paper Kingdom does and does not do", "Closing invitation"][index]}\n\nSubject: ${["Why good book ideas still look unrelated", "The page that holds a whole catalogue together", "Six books—without six disconnected design decisions", "Bring your own AI. Keep control of the work.", "Build the system before you build another cover"][index]}\n\nOpening: Name a familiar publishing decision without exaggerating the pain.\nBody: Teach one useful idea, show the relevant Paper Kingdom workflow, and give a concrete example.\nProof: Refer only to included modules, prompts, templates, or demonstrable product behavior.\nCTA: Invite the reader to review Paper Kingdom and decide whether the structured workflow fits their publishing process.\nCompliance: Do not promise acceptance, rankings, sales, or income.`,
    ),
  ),
  asset("reseller-license", "Reseller License", "Clear customer-delivery rights and prohibited uses.", "License", "paper-kingdom-reseller", `# Paper Kingdom Reseller License\n\nYou may sell the branded Paper Kingdom customer product and use the supplied reseller marketing materials.\n\nYou may not:\n- grant resale, private-label, master-resale, or source-code rights;\n- alter ownership notices or imply authorship of the application;\n- redistribute editable source code, protected libraries, or purchaser data;\n- make earnings, ranking, acceptance, or legal-compliance guarantees;\n- use third-party assets outside their original licence.\n\nYou are responsible for your customer support, sales claims, refunds, taxes, platform compliance, and secure delivery. This licence is non-exclusive and revocable for material breach.`),
  ...productionResources,
  ...launchGrowthResources,
];

export const seasonalCampaigns = [
  "New Year, Clear Direction",
  "Spring Reset",
  "Mother’s Day Keepsakes",
  "Summer Activity Season",
  "Back-to-School Focus",
  "Autumn Reflection",
  "Holiday Planning",
  "Giftable Guided Books",
  "Small Business Q4",
  "National Novel-Writing Season",
  "Year-in-Review",
  "Evergreen Relaunch",
].map((name, index) => ({
  id: `seasonal-${index + 1}`,
  name,
  requiredOffer: "paper-kingdom-unlimited" as const,
  brief: `Build a four-week ${name} campaign around one useful reader outcome, a catalogue bundle angle, and claim-safe educational content.`,
}));

export const offerLabels: Record<OfferSku, string> = {
  "paper-kingdom-fe": "Core tools",
  "paper-kingdom-unlimited": "More examples",
  "paper-kingdom-dfy": "Ready-made book plans",
  "paper-kingdom-production": "Book-making tools",
  "paper-kingdom-launch-growth": "Selling tools",
  "paper-kingdom-reseller": "Reseller licence",
};
